import React, { useState, useMemo, useEffect } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Expander from '@amzn/meridian/expander';
import Select, { SelectOption } from '@amzn/meridian/select';
import { TEXTS } from '../../utils/constants';
import { formatDuration } from './laborOpportunitiesModel';
import { matchProcessActuals } from './flowPlannerAdherenceModel';
import { GROUP_VOLUME_ACTUAL } from './FlowPlannerGridConfig';
import './shared/flowPlannerShared.css';

const METRICS = [
  { value: 'all', label: 'All Metrics' },
  { value: 'volume', label: 'Volume' },
  { value: 'rate', label: 'Rate' },
  { value: 'headcount', label: 'Headcount' }
];

// New-view groups that show volume only on parent rows; child paths drop it.
const NEW_VIEW = 'new';
const CLASSIC_VIEW = 'classic';
const PARENT_ONLY_VOLUME_GROUPS = new Set(['INDUCT_GROUP', 'PROBLEM_SOLVE', 'NON_CON_STOW', 'OUTBOUND']);

const DEPTH_INDENT_PX = { 0: 0, 1: 24, 2: 48 };
const formatNumber = (value) => (value === null ? 'N/A' : (value || 0).toLocaleString());
// Rate actuals arrive as fractional interval averages; show them rounded.
const formatRateActual = (v) => Math.round(v).toLocaleString();

const METRIC_FORMATTERS = {
  volume: (s) => formatNumber(s.volume),
  rate: (s) => formatNumber(s.rate),
  workTime: (s) => `${s.workTime || 0}m`,
  headcount: (s) => (s.headcount || 0).toFixed(2)
};

// ── Actuals (additive-only) ───────────────────────────────────────────────────
// Purely additive: a blue "actual /" fragment before the existing planned number.
// Never changes how a planned value is computed; emits nothing when actuals are
// hidden or the row has no matching adherence series.

// A row's matched actuals ({ volume, rate, headcount } arrays), or null.
function getRowActual(ctx, node) {
  if (!ctx || !ctx.showActuals || !ctx.byProcess) return null;
  const candidates = [GROUP_VOLUME_ACTUAL[node.key], node.actualsVolumeProcess, node.key, node.displayName].filter(
    Boolean
  );
  return matchProcessActuals(ctx.byProcess, candidates);
}

// undefined → no series for this row; null → series but no actual here ("—");
// number → the formatted value.
const actualAt = (arr, i) => (arr == null ? undefined : arr[i]);

// Blue "actual /" fragment; renders nothing when value === undefined.
const ActualFragment = ({ value, format, title }) => {
  if (value === undefined) return null;
  return (
    <>
      <span className="fp-plan-actual" title={title}>
        {value === null ? '—' : format(value)}
      </span>
      <span className="fp-plan-actual-sep" aria-hidden="true">
        /
      </span>
    </>
  );
};

const ToggleButton = ({ open, onClick, label }) => (
  <button type="button" className="fp-plan-toggle" onClick={onClick} aria-expanded={open} aria-label={label}>
    {open ? '▼' : '▶'}
  </button>
);

const ToggleSpacer = () => <span className="fp-plan-toggle-spacer" aria-hidden="true" />;

const PVT_BREACH_THRESHOLD = 0.95;

const PvtPill = ({ pvt }) => {
  if (!pvt) return null;
  const planned = pvt.planned || 0;
  const target = pvt.target || 0;
  if (!planned && !target) return null;
  // Only flag a breach when achievement falls below 95% — small overshoots
  // (e.g. 285/286) shouldn't read as alarm-level.
  const breach = target > 0 && planned / target < PVT_BREACH_THRESHOLD;
  return (
    <span className="fp-plan-pvt" data-breach={breach} title={`Planned ${planned} / Target ${target}`}>
      <span>{planned.toLocaleString()}</span>
      <span className="fp-plan-pvt-sep">/</span>
      <span>{target.toLocaleString()}</span>
    </span>
  );
};

const ThreeCellGrid = ({ slot, actual, ended, hideVolume = false }) => {
  // hideVolume drops the Vol line only; volume still counts towards emptiness so a
  // zero-by-config row reads "Rate 0 · HC 0.0" rather than "—" (no plan data).
  const allZero = !slot.volume && !slot.rate && !slot.headcount;
  // Only show the empty cell when there's no actual either, or the actual would
  // vanish. Headcount excluded — it's station-wide, so no longer surfaced here.
  const hasActual = actual && ((!hideVolume && actual.volume !== undefined) || actual.rate !== undefined);
  if (allZero && !hasActual) return <div className="fp-plan-mini-empty">—</div>;
  // Strike a planned metric only where its actual is shown and the window ended.
  const struck = (v) => (ended && v !== undefined ? 'fp-plan-struck' : undefined);
  return (
    <div className="fp-plan-mini-stack">
      {!hideVolume && (
        <span className="fp-plan-mini-primary">
          <span className="fp-plan-mini-label">Vol</span>
          {actual && <ActualFragment value={actual.volume} format={formatNumber} />}
          <span className={struck(actual?.volume)}>{formatNumber(slot.volume)}</span>
        </span>
      )}
      <span className="fp-plan-mini-secondary">
        <span className="fp-plan-mini-label">Rate</span>
        {actual && <ActualFragment value={actual.rate} format={formatRateActual} />}
        <span className={struck(actual?.rate)}>{formatNumber(slot.rate)}</span>
        <span className="sep" aria-hidden="true">
          ·
        </span>
        <span className="fp-plan-mini-label">HC</span>
        {/* HC actual omitted (station-wide); planned HC still strikes when ended. */}
        <span className={`fp-plan-mini-hc${ended ? ' fp-plan-struck' : ''}`}>{(slot.headcount || 0).toFixed(1)}</span>
      </span>
    </div>
  );
};

const SingleMetricCell = ({ slot, metric, actual, ended }) => {
  const formatted = METRIC_FORMATTERS[metric](slot);
  const empty = formatted === '0' || formatted === '0.00' || formatted === '0m';
  // HC actuals are station-wide, so only volume/rate carry one here.
  const actualValue = metric === 'headcount' ? undefined : actual ? actual[metric] : undefined;
  const actualFormat = metric === 'rate' ? formatRateActual : formatNumber;
  // Once ended: HC strikes its planned value directly, others only with an actual.
  const struck = ended && (metric === 'headcount' ? !empty : actualValue !== undefined);
  return (
    <span className={`fp-plan-value${empty && actualValue === undefined ? ' fp-plan-value-muted' : ''}`}>
      <ActualFragment value={actualValue} format={actualFormat} />
      <span className={struck ? 'fp-plan-struck' : undefined}>{empty ? '—' : formatted}</span>
    </span>
  );
};

const GroupRow = ({ row, depth, isOpen, onToggle, actualsCtx, ended, showVolLabel = false }) => {
  const isTopLevel = depth === 0;
  const rowActual = getRowActual(actualsCtx, row);
  return (
    <tr>
      <td className="fp-plan-cell fp-plan-row-label">
        <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
          <ToggleButton
            open={isOpen}
            onClick={onToggle}
            label={`${isOpen ? 'Collapse' : 'Expand'} ${row.displayName}`}
          />
          <span className="fp-plan-row-text">{row.displayName}</span>
        </span>
      </td>
      <td className="fp-plan-cell fp-plan-cell--center">
        <span className="fp-plan-value-muted">—</span>
      </td>
      {row.slots.map((slot, i) => {
        const empty = !slot.volume && !slot.headcount;
        const actualVol = rowActual ? actualAt(rowActual.volume, i) : undefined;
        return (
          <td key={i} className="fp-plan-cell fp-plan-cell--center">
            {empty && actualVol === undefined ? (
              <span className="fp-plan-value-muted">—</span>
            ) : (
              <span className={`fp-plan-value${isTopLevel ? ' is-emphasized' : ''}`}>
                {showVolLabel && <span className="fp-plan-mini-label">Vol</span>}
                <ActualFragment value={actualVol} format={formatNumber} />
                <span className={ended?.[i] && actualVol !== undefined ? 'fp-plan-struck' : undefined}>
                  {(slot.volume || 0).toLocaleString()}
                </span>
                {!isOpen && isTopLevel && (
                  <span className="fp-plan-aggregate-secondary">{`(${(slot.headcount || 0).toFixed(1)} HC)`}</span>
                )}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
};

const DetailRow = ({ row, depth, metric, actualsCtx, ended, hideVolume = false, isOpen, onToggle }) => {
  const rowActual = getRowActual(actualsCtx, row);
  const actualForSlot = (i) =>
    rowActual
      ? {
          volume: actualAt(rowActual.volume, i),
          rate: actualAt(rowActual.rate, i),
          headcount: actualAt(rowActual.headcount, i)
        }
      : null;
  return (
    <tr>
      <td className="fp-plan-cell fp-plan-row-label">
        <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
          {onToggle ? (
            <ToggleButton
              open={!!isOpen}
              onClick={onToggle}
              label={`${isOpen ? 'Collapse' : 'Expand'} ${row.displayName}`}
            />
          ) : (
            <ToggleSpacer />
          )}
          <span className="fp-plan-row-text">
            {row.displayName}
            {row.splitPercentage != null && (
              <span className="fp-plan-split-pct">{` (${row.splitPercentage.toFixed(0)}%)`}</span>
            )}
          </span>
        </span>
      </td>
      <td className="fp-plan-cell fp-plan-cell--center">
        {row.pvt ? <PvtPill pvt={row.pvt} /> : <span className="fp-plan-value-muted">—</span>}
      </td>
      {row.slots.map((slot, i) => (
        <td key={i} className="fp-plan-cell fp-plan-cell--center">
          {metric === 'all' ? (
            <ThreeCellGrid slot={slot} actual={actualForSlot(i)} ended={ended?.[i]} hideVolume={hideVolume} />
          ) : (
            <SingleMetricCell slot={slot} metric={metric} actual={actualForSlot(i)} ended={ended?.[i]} />
          )}
        </td>
      ))}
    </tr>
  );
};

const WipRow = ({ stowWIP, columnCount }) => (
  <tr className="fp-plan-wip-row">
    <td className="fp-plan-cell fp-plan-row-label">
      <span className="fp-plan-row-label-inner">
        <ToggleSpacer />
        <span className="fp-plan-wip-chip">WIP</span>
        <span className="fp-plan-row-text">Stow WIP</span>
      </span>
    </td>
    <td className="fp-plan-cell fp-plan-cell--center">
      <span className="fp-plan-value-muted">—</span>
    </td>
    {stowWIP.slice(0, columnCount).map((value, i) => (
      <td
        key={i}
        className="fp-plan-cell fp-plan-cell--center"
        title="Work In Progress: Cumulative Induct - Cumulative Stow">
        {value === 0 ? (
          <span className="fp-plan-value-muted">—</span>
        ) : (
          <span className="fp-plan-value is-emphasized">{value.toLocaleString()}</span>
        )}
      </td>
    ))}
  </tr>
);

function renderNode(
  node,
  depth,
  expanded,
  toggle,
  metric,
  actualsCtx,
  ended,
  hideVolume = false,
  showVolLabel = false
) {
  const isGroup = !!node.children;
  const key = node.key || node.displayName;
  const isOpen = expanded.includes(key);
  // Leaf DetailRows only; GroupRows always keep volume. Regrouped rows carry the flag.
  const hideVol = hideVolume || !!node.hideVolume;

  // A process row with children (a Stow variant) keeps its own PVT/rate/HC cells and
  // just gains a toggle. Synthetic aggregates have pvt === null → GroupRow.
  if (!isGroup || node.pvt) {
    const elements = [
      <DetailRow
        key={key}
        row={node}
        depth={depth}
        metric={metric}
        actualsCtx={actualsCtx}
        ended={ended}
        hideVolume={hideVol}
        isOpen={isGroup ? isOpen : undefined}
        onToggle={isGroup ? () => toggle(key) : undefined}
      />
    ];
    if (isGroup && isOpen) {
      node.children.forEach((child) => {
        elements.push(
          ...renderNode(child, depth + 1, expanded, toggle, metric, actualsCtx, ended, hideVol, showVolLabel)
        );
      });
    }
    return elements;
  }

  const elements = [
    <GroupRow
      key={key}
      row={node}
      depth={depth}
      isOpen={isOpen}
      onToggle={() => toggle(key)}
      actualsCtx={actualsCtx}
      ended={ended}
      showVolLabel={showVolLabel}
    />
  ];

  if (isOpen) {
    node.children.forEach((child) => {
      elements.push(
        ...renderNode(child, depth + 1, expanded, toggle, metric, actualsCtx, ended, hideVol, showVolLabel)
      );
    });
  }
  return elements;
}

function collectAllGroupKeys(nodes) {
  const keys = [];
  for (const node of nodes) {
    if (node.children && !node.hidden) {
      keys.push(node.key || node.displayName);
      keys.push(...collectAllGroupKeys(node.children));
    }
  }
  return keys;
}

const LaborPlanGrid = ({
  gridCycleData,
  intervals = [],
  actuals = null,
  showActuals = false,
  endedByInterval = []
}) => {
  const [expanded, setExpanded] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [isExpanded, setIsExpanded] = useState(true);
  const [gridView, setGridView] = useState(CLASSIC_VIEW);

  // New view drops Volume from the dropdown (3.2); it stays static at parent rows.
  const metricOptions = gridView === NEW_VIEW ? METRICS.filter((m) => m.value !== 'volume') : METRICS;

  // No Volume option in the new view, so fall back to All Metrics.
  const changeView = (view) => {
    setGridView(view);
    if (view === NEW_VIEW && selectedMetric === 'volume') setSelectedMetric('all');
  };

  const toggle = (key) => setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const { rows = [], stowWIP = [] } = gridCycleData || {};

  // New view regroups top-level rows by shared volume source (Classic untouched):
  // Induct Group <- Inbound Dock W/S + Manual Diverter (after Manual); Stow <- Pick to
  // Buffer nested under the variant sharing its execution mode, + Sort Problem Solve.
  // Rows inheriting a parent's volume are tagged hideVolume so it shows once.
  const displayRows = useMemo(() => {
    if (gridView !== NEW_VIEW) return rows;

    const induct = rows.find((r) => r.key === 'INDUCT_GROUP' && r.children);
    const stow = rows.find((r) => r.key === 'STOW' && r.children);
    const absorbed = new Set();
    const claim = (key, target, hideVolume = false) => {
      const row = target ? rows.find((r) => r.key === key) : null;
      if (!row) return [];
      absorbed.add(key);
      const leaves = row.children && row.children.length > 0 ? row.children : [row];
      return hideVolume ? leaves.map((node) => ({ ...node, hideVolume: true })) : leaves;
    };

    const waterSpiderLeaves = claim('WATER_SPIDER', induct);
    const divertLeaves = claim('DIVERT', induct, true);
    const pickToBufferLeaves = claim('PICK_TO_BUFFER', stow, true);
    const problemSolveLeaves = claim('PROBLEM_SOLVE', stow, true);
    if (absorbed.size === 0) return rows;

    return rows
      .filter((r) => !absorbed.has(r.key))
      .map((r) => {
        if (r === induct) {
          const children = [...waterSpiderLeaves, ...r.children];
          const afterManual = children.findIndex((c) => c.key === 'MECHANICAL') + 1;
          const at = afterManual > 0 ? afterManual : children.length;
          return { ...r, children: [...children.slice(0, at), ...divertLeaves, ...children.slice(at)] };
        }
        if (r === stow) {
          const nestedTypes = new Set();
          const variants = r.children.map((variant) => {
            const nested = pickToBufferLeaves.filter(
              (node) => node.executionType && node.executionType === variant.executionType
            );
            if (nested.length === 0) return variant;
            nestedTypes.add(variant.executionType);
            return { ...variant, children: nested };
          });
          // Anything with no matching variant stays visible at Stow level.
          const unmatched = pickToBufferLeaves.filter((node) => !nestedTypes.has(node.executionType));
          return { ...r, children: [...variants, ...unmatched, ...problemSolveLeaves] };
        }
        return r;
      });
  }, [rows, gridView]);

  const allGroupKeys = useMemo(() => collectAllGroupKeys(displayRows), [displayRows]);

  // With actuals off or absent, byProcess is undefined and getRowActual()
  // short-circuits, so planned output stays byte-for-byte identical.
  const actualsCtx = {
    showActuals: showActuals && !!actuals?.byProcess,
    byProcess: actuals?.byProcess
  };

  useEffect(() => setExpanded([]), [gridCycleData]);
  if (!gridCycleData) return null;

  const wipBeforeKey = 'STOW';
  const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every((k) => expanded.includes(k));
  const expandAll = () => setExpanded(allGroupKeys);
  const collapseAll = () => setExpanded([]);

  const slotCount = intervals.length || rows[0]?.slots.length || 0;

  return (
    <div className="fp-theme">
      <Column spacing="300" alignmentHorizontal="stretch" width="100%">
        <Expander open={isExpanded} onChange={setIsExpanded} title="Labour Plan" type="inline" spacingInset="300">
          <Column spacing="100" alignmentHorizontal="stretch" width="100%">
            <Row alignmentHorizontal="end" alignmentVertical="center" spacing="300" width="100%">
              <Text type={TEXTS.T3}>Layout:</Text>
              <span className="fp-grid-view-toggle" role="group" aria-label="Grid layout">
                <button
                  type="button"
                  className="fp-grid-view-btn"
                  aria-pressed={gridView === CLASSIC_VIEW}
                  onClick={() => changeView(CLASSIC_VIEW)}>
                  Classic
                </button>
                <button
                  type="button"
                  className="fp-grid-view-btn"
                  aria-pressed={gridView === NEW_VIEW}
                  onClick={() => changeView(NEW_VIEW)}>
                  New
                </button>
              </span>
              <Text type={TEXTS.T3}>View:</Text>
              <Select value={selectedMetric} onChange={setSelectedMetric} width={140} size="small">
                {metricOptions.map((m) => (
                  <SelectOption key={m.value} value={m.value} label={m.label} />
                ))}
              </Select>
            </Row>
            <div className="fp-plan-scroll">
              <table className="fp-plan-table">
                <thead>
                  <tr>
                    <th
                      className="fp-plan-header-cell fp-plan-header-cell--process"
                      scope="col"
                      aria-label="Process column">
                      <span className="fp-plan-row-label-inner">
                        <ToggleButton
                          open={allExpanded}
                          onClick={allExpanded ? collapseAll : expandAll}
                          label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                        />
                        <span>Process</span>
                      </span>
                    </th>
                    <th className="fp-plan-header-cell fp-plan-header-cell--pvt" scope="col">
                      PVT
                    </th>
                    {intervals.map((iv, idx) => (
                      <th
                        key={idx}
                        className={`fp-plan-header-cell fp-plan-header-cell--slot${
                          idx === slotCount - 1 ? ' is-last' : ''
                        }`}
                        scope="col">
                        <div className="fp-summary-slot-line">
                          <span className="fp-summary-slot-primary">{iv.name || `P${idx + 1}`}</span>
                          {iv.startLabel && iv.endLabel && (
                            <span className="fp-summary-slot-secondary">{`${iv.startLabel}–${iv.endLabel}`}</span>
                          )}
                          {iv.endTime && iv.startTime && (
                            <span className="fp-summary-slot-tertiary">
                              {formatDuration(iv.endTime - iv.startTime)}
                            </span>
                          )}
                        </div>
                        <div className="fp-summary-slot-indicator" data-status="info" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.flatMap((row) => {
                    if (row.hidden) return [];
                    // New view: listed groups drop volume on child paths.
                    const hideVol = gridView === NEW_VIEW && PARENT_ONLY_VOLUME_GROUPS.has(row.key);
                    const rendered = renderNode(
                      row,
                      0,
                      expanded,
                      toggle,
                      selectedMetric,
                      actualsCtx,
                      endedByInterval,
                      hideVol,
                      gridView === NEW_VIEW
                    );
                    if (row.key === wipBeforeKey && stowWIP.length > 0) {
                      return [<WipRow key="__wip__" stowWIP={stowWIP} columnCount={slotCount} />, ...rendered];
                    }
                    return rendered;
                  })}
                </tbody>
              </table>
            </div>
          </Column>
        </Expander>
      </Column>
    </div>
  );
};

export default LaborPlanGrid;
